import { Anchor, Badge, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core"
import { ArrowSquareOutIcon, TruckTrailerIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { IOrderPlus } from "../../models/Order"
import { OrderTypeBadge } from "../active-deliveries/atoms"

interface Props {
  order: IOrderPlus
}

const DELIVERY_STATUS_COLOR: Record<string, string> = {
  DRAFT     : "gray",
  ASSIGNED  : "blue",
  LOADED    : "blue",
  DELIVERING: "blue",
  IN_TRANSIT: "blue",
  DELIVERED : "teal",
  COMPLETED : "teal",
  CANCELLED : "red",
}

/**
 * R14 / G18e — Multi-delivery summary widget on Order detail.
 *
 * Renders only for consignment orders. Shows the linked DeliveryNote(s) with
 * type + status + tracking — gives the retailer (and supplier) immediate
 * visibility into the physical shipment lifecycle without leaving the
 * Захиалга detail page.
 *
 * Data source: `IOrderPlus.deliveryNote` (1:1 today; the widget renders an
 * array form so future split scenarios are forward-compatible).
 */
export function MultiDeliverySummaryWidget({ order }: Props) {
  const { t } = useTranslation("supplierSales")
  const orderType = order.orderType ?? "REGULAR"
  if (orderType === "REGULAR") return null

  const deliveries = order.deliveryNote ? [order.deliveryNote] : []
  if (deliveries.length === 0) {
    return (
      <Paper
        withBorder
        radius="md"
        p="md"
        mb="md"
        style={{
          borderColor: "var(--mantine-color-blue-3)",
          background : "var(--mantine-color-blue-0)",
        }}
      >
        <Group gap={10} align="center">
          <ThemeIcon size={28} radius={8} variant="light" color="blue">
            <TruckTrailerIcon size={14} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fz={14} fw={700} tt="uppercase" c="blue.7" style={{ letterSpacing: "0.08em" }}>
              {t("multiDelivery.pendingTitle")}
            </Text>
            <Text fz={14} c="dimmed">
              {t("multiDelivery.pendingHint")}
            </Text>
          </Stack>
        </Group>
      </Paper>
    )
  }

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mb="md"
      style={{
        borderColor: "var(--mantine-color-blue-3)",
        background : "var(--mantine-color-blue-0)",
      }}
    >
      <Stack gap="sm">
        <Group gap={10} align="center">
          <ThemeIcon size={28} radius={8} variant="light" color="blue">
            <TruckTrailerIcon size={14} />
          </ThemeIcon>
          <Stack gap={0}>
            <Text fz={14} fw={700} tt="uppercase" c="blue.7" style={{ letterSpacing: "0.08em" }}>
              {t("multiDelivery.statusTitle", { count: deliveries.length })}
            </Text>
            <Text fz={14} c="dimmed">
              {t("multiDelivery.arrivesIn", { count: deliveries.length })}
            </Text>
          </Stack>
        </Group>

        <Stack gap="xs">
          {deliveries.map((dn) => {
            const statusKey = dn.deliveryNoteStatus ?? "DRAFT"
            return (
              <Paper
                key={dn.id ?? dn.refCode}
                withBorder
                p="xs"
                radius="sm"
                style={{ background: "var(--mantine-color-white)" }}
              >
                <Group justify="space-between" wrap="wrap" align="center">
                  <Group gap="md" wrap="wrap">
                    <Stack gap={2}>
                      <Text fz={14} c="dimmed" tt="uppercase" fw={600}>
                        DN #
                      </Text>
                      <Text fz={14} fw={600} ff="monospace">
                        {dn.refCode ?? dn.id?.slice(0, 8) ?? "—"}
                      </Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text fz={14} c="dimmed" tt="uppercase" fw={600}>
                        {t("multiDelivery.type")}
                      </Text>
                      <OrderTypeBadge
                        orderType={(dn as { orderType?: string | null }).orderType as never}
                        source={(dn as { source?: string | null }).source as never}
                        size="sm"
                      />
                    </Stack>
                    <Stack gap={2}>
                      <Text fz={14} c="dimmed" tt="uppercase" fw={600}>
                        {t("multiDelivery.status")}
                      </Text>
                      <Badge
                        color={DELIVERY_STATUS_COLOR[statusKey] ?? "gray"}
                        variant="light"
                        size="sm"
                      >
                        {t(`multiDelivery.statusLabel.${statusKey}`, { defaultValue: statusKey })}
                      </Badge>
                    </Stack>
                    {dn.deliveryDate && (
                      <Stack gap={2}>
                        <Text fz={14} c="dimmed" tt="uppercase" fw={600}>
                          {t("multiDelivery.deliver")}
                        </Text>
                        <Text fz={14} style={{ fontVariantNumeric: "tabular-nums" }}>
                          {dayjs(dn.deliveryDate).format("YYYY-MM-DD")}
                        </Text>
                      </Stack>
                    )}
                  </Group>

                  {dn.id && (
                    <Anchor
                      component={Link}
                      to={`/supplier-delivery/detail/${dn.id}`}
                      size="sm"
                    >
                      <Group gap={4} wrap="nowrap">
                        {t("multiDelivery.detail")}
                        <ArrowSquareOutIcon size={12} />
                      </Group>
                    </Anchor>
                  )}
                </Group>
              </Paper>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
